import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { GripVertical, Loader2, Plus, Save, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';

type PathSelectionFormData = {
    pageTitleDe: string;
    pageTitleEn: string;
    progressDisplayDe: string;
    progressDisplayEn: string;
    path1TitleDe: string;
    path1TitleEn: string;
    path1DescriptionDe: string;
    path1DescriptionEn: string;
    path2TitleDe: string;
    path2TitleEn: string;
    path2DescriptionDe: string;
    path2DescriptionEn: string;
    doctorSectionTitleDe: string;
    doctorSectionTitleEn: string;
    doctorSectionTextDe: string;
    doctorSectionTextEn: string;
    loginPromptTextDe: string;
    loginPromptTextEn: string;
    loginPromptLinkTextDe: string;
    loginPromptLinkTextEn: string;
    continueButtonTextDe: string;
    continueButtonTextEn: string;
};

type DoctorImage = {
    id: string;
    url: string;
    order: number;
    altText: string | null;
};

const PathSelectionCMS = () => {
    const [formData, setFormData] = useState<PathSelectionFormData>({
        pageTitleDe: '',
        pageTitleEn: '',
        progressDisplayDe: '',
        progressDisplayEn: '',
        path1TitleDe: '',
        path1TitleEn: '',
        path1DescriptionDe: '',
        path1DescriptionEn: '',
        path2TitleDe: '',
        path2TitleEn: '',
        path2DescriptionDe: '',
        path2DescriptionEn: '',
        doctorSectionTitleDe: '',
        doctorSectionTitleEn: '',
        doctorSectionTextDe: '',
        doctorSectionTextEn: '',
        loginPromptTextDe: '',
        loginPromptTextEn: '',
        loginPromptLinkTextDe: '',
        loginPromptLinkTextEn: '',
        continueButtonTextDe: '',
        continueButtonTextEn: '',
    });
    const [isDirty, setIsDirty] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const utils = trpc.useUtils();

    const { data: configData, isLoading: isLoadingConfig } = trpc.bookingFunnelCMS.getPathSelectionConfig.useQuery();

    const updateConfigMutation = trpc.bookingFunnelCMS.updatePathSelectionConfig.useMutation({
        onSuccess: () => {
            toast.success('Konfiguration gespeichert');
            setIsDirty(false);
            utils.bookingFunnelCMS.getPathSelectionConfig.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern', { description: error.message });
        },
    });

    const uploadImageMutation = trpc.bookingFunnelCMS.uploadDoctorImage.useMutation({
        onSuccess: () => {
            toast.success('Bild hochgeladen');
            utils.bookingFunnelCMS.getPathSelectionConfig.invalidate();
            setIsUploading(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Hochladen', { description: error.message });
            setIsUploading(false);
        },
    });

    const deleteImageMutation = trpc.bookingFunnelCMS.deleteDoctorImage.useMutation({
        onSuccess: () => {
            toast.success('Bild gelöscht');
            utils.bookingFunnelCMS.getPathSelectionConfig.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Löschen', { description: error.message });
        },
    });

    const reorderImagesMutation = trpc.bookingFunnelCMS.reorderDoctorImages.useMutation({
        onSuccess: () => {
            utils.bookingFunnelCMS.getPathSelectionConfig.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Sortieren', { description: error.message });
        },
    });

    useEffect(() => {
        if (configData?.data) {
            const config = configData.data;
            setFormData({
                pageTitleDe: config.pageTitleDe,
                pageTitleEn: config.pageTitleEn,
                progressDisplayDe: config.progressDisplayDe,
                progressDisplayEn: config.progressDisplayEn,
                path1TitleDe: config.path1TitleDe,
                path1TitleEn: config.path1TitleEn,
                path1DescriptionDe: config.path1DescriptionDe,
                path1DescriptionEn: config.path1DescriptionEn,
                path2TitleDe: config.path2TitleDe,
                path2TitleEn: config.path2TitleEn,
                path2DescriptionDe: config.path2DescriptionDe,
                path2DescriptionEn: config.path2DescriptionEn,
                doctorSectionTitleDe: config.doctorSectionTitleDe,
                doctorSectionTitleEn: config.doctorSectionTitleEn,
                doctorSectionTextDe: config.doctorSectionTextDe,
                doctorSectionTextEn: config.doctorSectionTextEn,
                loginPromptTextDe: config.loginPromptTextDe,
                loginPromptTextEn: config.loginPromptTextEn,
                loginPromptLinkTextDe: config.loginPromptLinkTextDe,
                loginPromptLinkTextEn: config.loginPromptLinkTextEn,
                continueButtonTextDe: config.continueButtonTextDe,
                continueButtonTextEn: config.continueButtonTextEn,
            });
        }
    }, [configData]);

    const handleInputChange = (field: keyof PathSelectionFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setIsDirty(true);
    };

    const handleSave = () => {
        updateConfigMutation.mutate(formData);
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error('Nur Bilder sind erlaubt');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Bild ist zu groß (max. 10MB)');
            return;
        }

        setIsUploading(true);

        // Convert to base64
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result as string;
            uploadImageMutation.mutate({ imageBase64: base64 });
        };
        reader.onerror = () => {
            toast.error('Fehler beim Lesen der Datei');
            setIsUploading(false);
        };
        reader.readAsDataURL(file);

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDeleteImage = (imageId: string) => {
        if (confirm('Möchten Sie dieses Bild wirklich löschen?')) {
            deleteImageMutation.mutate({ imageId });
        }
    };

    const handleMoveImage = (imageId: string, direction: 'up' | 'down') => {
        const images = configData?.data?.doctorImages || [];
        const currentIndex = images.findIndex((img) => img.id === imageId);
        
        if (currentIndex === -1) {
            return;
        }

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        
        if (newIndex < 0 || newIndex >= images.length) {
            return;
        }

        // Create new order
        const newImages = [...images];
        const [movedImage] = newImages.splice(currentIndex, 1);
        newImages.splice(newIndex, 0, movedImage);

        // Update orders
        const imageOrders = newImages.map((img, index) => ({
            id: img.id,
            order: index,
        }));

        reorderImagesMutation.mutate({ imageOrders });
    };

    const doctorImages: DoctorImage[] = configData?.data?.doctorImages || [];
    const canAddMoreImages = doctorImages.length < 5;

    if (isLoadingConfig) {
        return (
            <div className='space-y-6'>
                <Skeleton className='h-[600px] w-full' />
                <Skeleton className='h-[300px] w-full' />
            </div>
        );
    }

    return (
        <div className='space-y-6'>
            {/* Copywriting Section */}
            <Card>
                <CardHeader>
                    <CardTitle>Texte & Einstellungen</CardTitle>
                    <CardDescription>
                        Bearbeiten Sie die Texte für die Pfad-Auswahl Seite.
                    </CardDescription>
                </CardHeader>
                <CardContent className='space-y-6'>
                    {/* Page Title */}
                    <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <Label htmlFor='pageTitleDe'>Seitentitel (Deutsch)</Label>
                            <Input
                                id='pageTitleDe'
                                value={formData.pageTitleDe}
                                onChange={(e) => handleInputChange('pageTitleDe', e.target.value)}
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label htmlFor='pageTitleEn'>Seitentitel (Englisch)</Label>
                            <Input
                                id='pageTitleEn'
                                value={formData.pageTitleEn}
                                onChange={(e) => handleInputChange('pageTitleEn', e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Progress Display */}
                    <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <Label htmlFor='progressDisplayDe'>Fortschrittsanzeige (Deutsch)</Label>
                            <Input
                                id='progressDisplayDe'
                                value={formData.progressDisplayDe}
                                onChange={(e) => handleInputChange('progressDisplayDe', e.target.value)}
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label htmlFor='progressDisplayEn'>Fortschrittsanzeige (Englisch)</Label>
                            <Input
                                id='progressDisplayEn'
                                value={formData.progressDisplayEn}
                                onChange={(e) => handleInputChange('progressDisplayEn', e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Path 1 */}
                    <div className='border rounded-lg p-4 space-y-4'>
                        <h4 className='font-medium'>Pfad 1: Erstbesuch</h4>
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='space-y-2'>
                                <Label htmlFor='path1TitleDe'>Titel (Deutsch)</Label>
                                <Input
                                    id='path1TitleDe'
                                    value={formData.path1TitleDe}
                                    onChange={(e) => handleInputChange('path1TitleDe', e.target.value)}
                                />
                            </div>
                            <div className='space-y-2'>
                                <Label htmlFor='path1TitleEn'>Titel (Englisch)</Label>
                                <Input
                                    id='path1TitleEn'
                                    value={formData.path1TitleEn}
                                    onChange={(e) => handleInputChange('path1TitleEn', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='space-y-2'>
                                <Label htmlFor='path1DescriptionDe'>Beschreibung (Deutsch)</Label>
                                <Textarea
                                    id='path1DescriptionDe'
                                    value={formData.path1DescriptionDe}
                                    onChange={(e) => handleInputChange('path1DescriptionDe', e.target.value)}
                                    rows={3}
                                />
                            </div>
                            <div className='space-y-2'>
                                <Label htmlFor='path1DescriptionEn'>Beschreibung (Englisch)</Label>
                                <Textarea
                                    id='path1DescriptionEn'
                                    value={formData.path1DescriptionEn}
                                    onChange={(e) => handleInputChange('path1DescriptionEn', e.target.value)}
                                    rows={3}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Path 2 */}
                    <div className='border rounded-lg p-4 space-y-4'>
                        <h4 className='font-medium'>Pfad 2: Behandlung auswählen</h4>
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='space-y-2'>
                                <Label htmlFor='path2TitleDe'>Titel (Deutsch)</Label>
                                <Input
                                    id='path2TitleDe'
                                    value={formData.path2TitleDe}
                                    onChange={(e) => handleInputChange('path2TitleDe', e.target.value)}
                                />
                            </div>
                            <div className='space-y-2'>
                                <Label htmlFor='path2TitleEn'>Titel (Englisch)</Label>
                                <Input
                                    id='path2TitleEn'
                                    value={formData.path2TitleEn}
                                    onChange={(e) => handleInputChange('path2TitleEn', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='space-y-2'>
                                <Label htmlFor='path2DescriptionDe'>Beschreibung (Deutsch)</Label>
                                <Textarea
                                    id='path2DescriptionDe'
                                    value={formData.path2DescriptionDe}
                                    onChange={(e) => handleInputChange('path2DescriptionDe', e.target.value)}
                                    rows={3}
                                />
                            </div>
                            <div className='space-y-2'>
                                <Label htmlFor='path2DescriptionEn'>Beschreibung (Englisch)</Label>
                                <Textarea
                                    id='path2DescriptionEn'
                                    value={formData.path2DescriptionEn}
                                    onChange={(e) => handleInputChange('path2DescriptionEn', e.target.value)}
                                    rows={3}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Doctor Section */}
                    <div className='border rounded-lg p-4 space-y-4'>
                        <h4 className='font-medium'>Ärzte-Sektion</h4>
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='space-y-2'>
                                <Label htmlFor='doctorSectionTitleDe'>Titel (Deutsch)</Label>
                                <Input
                                    id='doctorSectionTitleDe'
                                    value={formData.doctorSectionTitleDe}
                                    onChange={(e) => handleInputChange('doctorSectionTitleDe', e.target.value)}
                                />
                            </div>
                            <div className='space-y-2'>
                                <Label htmlFor='doctorSectionTitleEn'>Titel (Englisch)</Label>
                                <Input
                                    id='doctorSectionTitleEn'
                                    value={formData.doctorSectionTitleEn}
                                    onChange={(e) => handleInputChange('doctorSectionTitleEn', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className='grid grid-cols-2 gap-4'>
                            <div className='space-y-2'>
                                <Label htmlFor='doctorSectionTextDe'>Text (Deutsch)</Label>
                                <Textarea
                                    id='doctorSectionTextDe'
                                    value={formData.doctorSectionTextDe}
                                    onChange={(e) => handleInputChange('doctorSectionTextDe', e.target.value)}
                                    rows={3}
                                />
                            </div>
                            <div className='space-y-2'>
                                <Label htmlFor='doctorSectionTextEn'>Text (Englisch)</Label>
                                <Textarea
                                    id='doctorSectionTextEn'
                                    value={formData.doctorSectionTextEn}
                                    onChange={(e) => handleInputChange('doctorSectionTextEn', e.target.value)}
                                    rows={3}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Login Prompt */}
                    <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <Label htmlFor='loginPromptTextDe'>Login-Hinweis Text (Deutsch)</Label>
                            <Input
                                id='loginPromptTextDe'
                                value={formData.loginPromptTextDe}
                                onChange={(e) => handleInputChange('loginPromptTextDe', e.target.value)}
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label htmlFor='loginPromptTextEn'>Login-Hinweis Text (Englisch)</Label>
                            <Input
                                id='loginPromptTextEn'
                                value={formData.loginPromptTextEn}
                                onChange={(e) => handleInputChange('loginPromptTextEn', e.target.value)}
                            />
                        </div>
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <Label htmlFor='loginPromptLinkTextDe'>Login-Link Text (Deutsch)</Label>
                            <Input
                                id='loginPromptLinkTextDe'
                                value={formData.loginPromptLinkTextDe}
                                onChange={(e) => handleInputChange('loginPromptLinkTextDe', e.target.value)}
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label htmlFor='loginPromptLinkTextEn'>Login-Link Text (Englisch)</Label>
                            <Input
                                id='loginPromptLinkTextEn'
                                value={formData.loginPromptLinkTextEn}
                                onChange={(e) => handleInputChange('loginPromptLinkTextEn', e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Continue Button */}
                    <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <Label htmlFor='continueButtonTextDe'>Weiter-Button (Deutsch)</Label>
                            <Input
                                id='continueButtonTextDe'
                                value={formData.continueButtonTextDe}
                                onChange={(e) => handleInputChange('continueButtonTextDe', e.target.value)}
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label htmlFor='continueButtonTextEn'>Weiter-Button (Englisch)</Label>
                            <Input
                                id='continueButtonTextEn'
                                value={formData.continueButtonTextEn}
                                onChange={(e) => handleInputChange('continueButtonTextEn', e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Save Button */}
                    <div className='flex justify-end pt-4'>
                        <Button onClick={handleSave} disabled={!isDirty || updateConfigMutation.isPending}>
                            {updateConfigMutation.isPending ? (
                                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            ) : (
                                <Save className='mr-2 h-4 w-4' />
                            )}
                            Speichern
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Doctor Images Section */}
            <Card>
                <CardHeader>
                    <CardTitle>Ärzte-Bilder</CardTitle>
                    <CardDescription>
                        Laden Sie bis zu 5 Bilder hoch, die als Avatar-Bilder der Ärzte angezeigt werden.
                        Die Bilder werden automatisch auf 80x80 Pixel zugeschnitten.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className='space-y-4'>
                        {/* Image Gallery */}
                        <div className='flex flex-wrap gap-4'>
                            {doctorImages.map((image, index) => (
                                <div
                                    key={image.id}
                                    className='relative group border rounded-lg p-2 bg-gray-50'
                                >
                                    <img
                                        src={image.url}
                                        alt={image.altText || `Arzt ${index + 1}`}
                                        className='w-20 h-20 rounded-full object-cover'
                                    />
                                    <div className='absolute inset-0 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 rounded-lg'>
                                        {index > 0 && (
                                            <Button
                                                variant='ghost'
                                                size='icon'
                                                className='h-8 w-8 text-white hover:bg-white/20'
                                                onClick={() => handleMoveImage(image.id, 'up')}
                                            >
                                                <GripVertical className='h-4 w-4 rotate-90' />
                                            </Button>
                                        )}
                                        <Button
                                            variant='ghost'
                                            size='icon'
                                            className='h-8 w-8 text-white hover:bg-red-500/50'
                                            onClick={() => handleDeleteImage(image.id)}
                                        >
                                            <Trash2 className='h-4 w-4' />
                                        </Button>
                                        {index < doctorImages.length - 1 && (
                                            <Button
                                                variant='ghost'
                                                size='icon'
                                                className='h-8 w-8 text-white hover:bg-white/20'
                                                onClick={() => handleMoveImage(image.id, 'down')}
                                            >
                                                <GripVertical className='h-4 w-4 -rotate-90' />
                                            </Button>
                                        )}
                                    </div>
                                    <div className='text-center mt-1 text-xs text-muted-foreground'>
                                        #{index + 1}
                                    </div>
                                </div>
                            ))}

                            {/* Upload Button */}
                            {canAddMoreImages && (
                                <div
                                    className='w-20 h-20 border-2 border-dashed rounded-full flex items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors'
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    {isUploading ? (
                                        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
                                    ) : (
                                        <Plus className='h-6 w-6 text-muted-foreground' />
                                    )}
                                </div>
                            )}
                        </div>

                        <input
                            ref={fileInputRef}
                            type='file'
                            accept='image/*'
                            className='hidden'
                            onChange={handleFileSelect}
                        />

                        {!canAddMoreImages && (
                            <p className='text-sm text-muted-foreground'>
                                Maximale Anzahl von 5 Bildern erreicht.
                            </p>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default PathSelectionCMS;
